import React, { useEffect, useRef, useState } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { useMapSas } from "../../../hooks/useMapSas";
import { IAirportMap } from "../../../models/airports/AirportConfig";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { mapApiUrl } from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../../osm-maps/DisplayMapErrors";
import MapLibreMap from "../core/MapLibreMap";
import { useMapFailures } from "../hooks/useMapFailures";
import AirportMarker from "../map-markers/AirportMarker";
import { isValidLatLng, toLngLat } from "../utils/coordinates";
import { mapDisplayErrorStyles, mapLibreStyles } from "./mapStyles";

export interface MapDisplayAirportsProps {
  airportMapDetails: IAirportMap[];
}

const MapDisplayAirports: React.FC<MapDisplayAirportsProps> = ({
  airportMapDetails = [],
}) => {
  const styles = mapLibreStyles();
  const errorStyles = mapDisplayErrorStyles();
  const mapRef = useRef<MapRef>(null);
  const airport = useAppSelector((state) => getAirportCode(state));
  const airportConfig = getAirportConfig(airport);
  const [mapKey, setMapKey] = useState(0);
  const { mapApiFailures, handleMapApiFailure } = useMapFailures();

  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airport);
  const azureMapUrl = getAzureMapTileUrl(airport, mapApiUrl, "BASE", mapSas);

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setMapKey((value) => value + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  const validAirportDetails = airportMapDetails.filter((airportDetail) =>
    isValidLatLng(airportDetail.location)
  );
  const mapCenter = isValidLatLng(airportConfig.location)
    ? (airportConfig.location as [number, number])
    : (validAirportDetails[0]?.location ?? [0, 0]);

  return (
    <section
      className={styles.mapContainer}
      data-testid="maplibre-multi-airport-map"
    >
      <MapLibreMap
        key={mapKey}
        mapRef={mapRef}
        center={mapCenter}
        baseMapUrl={mapSas ? azureMapUrl : undefined}
        onMapApiFailure={handleMapApiFailure}
        onAuthError={refreshMapSas}
        testId="maplibre-multi-airport-canvas"
      >
        {validAirportDetails.map((airportDetail) => (
          <AirportMarker
            key={airportDetail.id}
            code={airportDetail.code}
            name={airportDetail.name}
            position={airportDetail.location}
            onClick={() =>
              mapRef.current?.flyTo({
                center: toLngLat(airportDetail.location),
                zoom: 13,
                duration: 1000,
              })
            }
          />
        ))}
        <DisplayMapErrors
          messages={mapApiFailures}
          className={errorStyles.airportMap}
        />
      </MapLibreMap>
    </section>
  );
};

export default MapDisplayAirports;
